import { List, Text } from '@mantine/core';
import type { ReactNode } from 'react';

/**
 * 每個頁面的「這是做什麼的」內容。UI 在 header 旁放 HelpPopover 顯示。
 * 內容請用「工讀生第一次進來看」的角度寫：不用技術術語（「aggregate」
 * 「domain event」），用功能動詞（「建訂位」「看客人名單」）。
 *
 * 這是 SSOT — 各頁直接 import 這裡的常數而非自己寫；集中改一個地方
 * 全站生效。
 */
export interface PageHelp {
  readonly title: string;
  readonly body: ReactNode;
}

export const PAGE_HELP: Record<string, PageHelp> = {
  dashboard: {
    title: '今日總覽',
    body: (
      <>
        <Text size="sm" c="dimmed">
          即時狀態面板 — 今天起 24 小時內所有訂位、以及各區座位使用狀況。
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>上方數字卡：待辦 / 已入座 / 已完成 / 取消。</List.Item>
          <List.Item>「時間軸」：按小時看訂位密度。</List.Item>
          <List.Item>「今日訂位」：點任一筆可 check-in / 結帳 / 改期。</List.Item>
        </List>
      </>
    ),
  },
  reservations: {
    title: '訂位列表',
    body: (
      <>
        <Text size="sm" c="dimmed">
          所有訂位（包含歷史）的可搜尋清單。用來找舊訂位、對帳、匯出。
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>上方篩選：狀態 / 日期區間 / 客人姓名或電話。</List.Item>
          <List.Item>「匯出 CSV」：當前篩選結果下載成試算表。</List.Item>
          <List.Item>點任一列開啟詳細頁，可編輯 / 取消 / 補款。</List.Item>
        </List>
      </>
    ),
  },
  customers: {
    title: '客戶管理',
    body: (
      <>
        <Text size="sm" c="dimmed">
          所有曾訂過位的客人名單。系統會自動累計造訪次數、no-show、消費金額。
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>VIP / blacklist 標籤達到自動規則門檻時自動套上。</List.Item>
          <List.Item>可合併重複客人（同一個人用了不同聯絡方式訂位）。</List.Item>
          <List.Item>封存（archive）只是隱藏，歷史訂位仍保留。</List.Item>
        </List>
      </>
    ),
  },
  analytics: {
    title: '營運分析',
    body: (
      <>
        <Text size="sm" c="dimmed">
          看整體生意狀況：訂位趨勢、座位使用率、取消/no-show 率、客群分佈。
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>上方時間切換：近 7 / 30 / 90 天。</List.Item>
          <List.Item>「訂位來源」：公開網站 / 電話 / LINE / walk-in 比例。</List.Item>
          <List.Item>資料來自「已完成」或「已取消」的訂位 — 未來訂位不列入。</List.Item>
        </List>
      </>
    ),
  },
  audit: {
    title: '操作稽核',
    body: (
      <>
        <Text size="sm" c="dimmed">
          誰在什麼時候改了什麼 — 用來追查「客人的訂位為什麼被取消」「定價是誰改的」這類問題。
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>所有訂位 / 客戶 / 設定變更都留紀錄。</List.Item>
          <List.Item>可依「對象 ID」「操作類型」「時間」篩選。</List.Item>
          <List.Item>紀錄無法刪除 — 合規要求。</List.Item>
        </List>
      </>
    ),
  },
  notifications: {
    title: '通知紀錄',
    body: (
      <>
        <Text size="sm" c="dimmed">
          LINE / email 送給客人的訊息都在這；失敗的可以看原因。
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>狀態：pending / sent / awaiting_recipient（等客人加 bot 好友）/ failed。</List.Item>
          <List.Item>「awaiting_recipient」不算失敗 — 客人加好友後下一 tick 會重送。</List.Item>
          <List.Item>「failed」通常是 provider 設定或 token 過期，去「設定 → 通知」tab 檢查。</List.Item>
        </List>
      </>
    ),
  },
  settings: {
    title: '店家設定',
    body: (
      <>
        <Text size="sm" c="dimmed">
          場館資訊、通知 provider 憑證、自動規則、流量限制。所有需要店家自己改的都在這。
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>「通知」tab 填完 LINE / Brevo 才會真的送訊息給客人。</List.Item>
          <List.Item>「自動規則」改完立即生效（VIP 門檻 / 黑名單門檻）。</List.Item>
          <List.Item>「流量限制」控制公開訂位 portal 的 spam 防護，請謹慎調整。</List.Item>
        </List>
      </>
    ),
  },
};
